import { Box, useTheme } from "@mui/material"
import { HorizontalBarChartContentReports } from "../../../theme/charts/HorizontalBarChart"
import { tokens } from "../../../../theme"
import { useReportHubFilters } from "../ReportHubFilterContext"
import { memo, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import Text from "../../../theme/text/Text"
import { RhReportCategory } from "../components/ReportDetailsPageDetails"
import { translateChartCategory } from "../reportUtils"
import Subheading from "../../../theme/text/Subheading"
import { ErrorBoundary } from "react-error-boundary"
import { ChartError } from "../../../theme/charts/Status/ChartError"
import { ChartLegendPopover } from "./components/ChartLegendPopover"

export const RHBarChart = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const legendRef = useRef(null);
    const itemRefs = useRef([]);
    const measurerRef = useRef(null);

    const [visibleCount, setVisibleCount] = useState(props.values?.length || 0);
    const [measured, setMeasured] = useState(false);
    const [hiddenCount, setHiddenCount] = useState(0);
    const [forceRemeasure, setForceRemeasure] = useState(0);

    // Memoize the legend items to display
    // Always show all items initially, then hide overflow after measurement
    const legendItems = useMemo(() => {
        if (!measured) return props.values; // Show all during initial render
        return props.values?.slice(0, visibleCount);
    }, [props.values, visibleCount, measured]);

    // Calculate totals for each series
    const seriesTotals = useMemo(() => {
        if (!props.values) return [];
        return props.values.map(series => {
            const total = series.data.reduce((sum, value) => sum + (value || 0), 0);
            return total;
        });
    }, [props.values]);

    useEffect(() => {
        let resizeTimeout;
        const resizeObserver = new ResizeObserver(() => {
            // Debounce resize to avoid excessive recalculations
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                setForceRemeasure(prev => prev + 1);
            }, 100);
        });
        if (legendRef.current) {
            resizeObserver.observe(legendRef.current);
        }
        return () => {
            clearTimeout(resizeTimeout);
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!legendRef.current || !props.values) return;

        // Use requestAnimationFrame to avoid blocking the main thread
        const rafId = requestAnimationFrame(() => {
            if (!legendRef.current || !props.values) return;

            const containerWidth = legendRef.current.offsetWidth;
            const totalItems = props.values?.length;

            // Use measurer for intrinsic width
            const moreWidth = measurerRef.current ? measurerRef.current.offsetWidth + 16 : 50;

            let usedWidth = 0;
            let count = 0;

            // First pass: calculate how many items fit
            for (let i = 0; i < totalItems; i++) {
                const el = itemRefs.current[i];
                if (!el) continue;
                const width = el.offsetWidth + 16; // gap

                // Check if adding this item + moreWidth (if not last possible) would overflow
                if (usedWidth + width + (i < totalItems - 1 ? moreWidth : 0) > containerWidth) {
                    break;
                }

                usedWidth += width;
                count++;
            }

            let finalHiddenCount = totalItems - count;

            // If hidden items exist, recheck if we need to hide one more item to fit the "+X more" button
            if (finalHiddenCount > 0 && usedWidth + moreWidth > containerWidth && count > 0) {
                const lastItemWidth = itemRefs.current[count - 1].offsetWidth + 16;
                usedWidth -= lastItemWidth;
                count--;
                finalHiddenCount = totalItems - count;
            }

            // Only update state if values actually changed to prevent infinite loops
            setVisibleCount(prev => prev !== count ? count : prev);
            setHiddenCount(prev => prev !== finalHiddenCount ? finalHiddenCount : prev);
            setMeasured(true);
        });

        return () => cancelAnimationFrame(rafId);
    }, [props.values, forceRemeasure]);
    return (
        <Box
            sx={{
                position:'relative',
                display:'flex',
                alignItems:'stretch',
                pr:3,
                mt:2,
                "@media screen and (max-width: 768px)" : {
                    pr:0,
                    flexDirection:'column',
                },
                ...props.sx
                // overflowX: 'auto',      // <-- enable horizontal scroll
                // overflowY: 'hidden',    // optional
            }}
        >

            {!props.isLoading && !props.isError && props.values?.length > 0 ?
                <Box
                    sx={{
                        position:"absolute",
                        right:props.right || 24,
                        top:props.top ||  "-56px",
                        height:40,
                        display:"inline-flex",
                        alignItems:"center",
                        background:colors.grey[200],
                        // background: chartTokens[props.color][colorType][100],
                        padding:0.5,
                        borderRadius:5,
                        gap:0.5,
                        zIndex:120,

                        "@media screen and (max-width: 1024px)" : {
                            position:'static',
                            order:2,
                            marginTop: "-24px",
                            mr:3,
                            pr:1,
                        },
                        "@media screen and (max-width: 768px)" : {
                            position:'static',
                            mr:0,
                            pr:0,
                        },
                        ...props.sx?.legend
                        // "@media screen and (max-width: 47.9375em)" : {
                        //     position:"relative",
                        //     display: props.isModal ? "flex" : "none",
                        //     top:"unset",
                        //     width:"calc(100% - 24px)",
                        //     // marginRight:3,
                        //     marginTop:2,
                        //     marginLeft:3
                        // }
                    }}
                >

                    <Box
                        ref={legendRef}
                        sx={{
                            height: 32,
                            px: 2,
                            pr: 0.5,
                            background: colors.grey[100],
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent:'center',
                            gap: 2,
                            width: 400,
                            overflow: "hidden", // important

                            "@media screen and (max-width: 1024px)": {
                                flex: 1,
                                mr: 0.5,
                            },
                            ...props.sx?.legend?.inner
                        }}
                    >
                        {legendItems?.map((series, index) => (
                                <Box
                                    key={index}
                                    ref={(el) => (itemRefs.current[index] = el)}
                                    sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    whiteSpace: "nowrap",
                                    }}
                                >
                                    <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        backgroundColor: props.staticColors[index],
                                    }}
                                    />
                                    <Text fontSize={12} noBreak>
                                        {t(series.name) ?? t(series.description)}
                                    </Text>
                                    <Text fontSize={12} noBreak bold>
                                        {seriesTotals[index]?.toLocaleString() ?? 0}
                                    </Text>
                                </Box>
                            ))
                        }

                        {measured && hiddenCount > 0 && (
                            <ChartLegendPopover
                                hiddenCount={hiddenCount}
                                values={props.values}
                                staticColors={props.staticColors}
                                seriesTotals={seriesTotals}
                            />
                        )}

                        <Box
                            ref={measurerRef}
                            sx={{
                                position: 'absolute',
                                visibility: 'hidden',
                                fontSize: 12,
                                px: 1,
                                whiteSpace: "nowrap",
                                fontWeight:500,
                                fontFamily:"Roboto, sans-serif",
                                color:colors.txt["primary"],
                            }}
                        >
                            +{hiddenCount} {t("more")}
                        </Box>
                    </Box>
                </Box>
            :null}

            <ErrorBoundary
                FallbackComponent={() => <ChartError sx={{left:0,borderRadius:4,}} onRetry={props.onRetry}/>}
                onError={(error, info) => console.error(error, info)}
            >
                {props.currentReport.category.use ?
                    <RHBarChartCategory
                        isLoading={props.isLoading}
                        isError={props.isError}
                        chartSettings={props.chartSettings}
                        values={props.values}
                        categories={props.categories}
                        staticColors={props.staticColors}
                        currentReport={props.currentReport}
                        setCurrentReport={props.setCurrentReport}
                        chartOption={props.chartOption}
                        onRetry={props.onRetry}
                    />
                :
                    <RHBarCharDates
                        isLoading={props.isLoading}
                        preview={props.preview}
                        isError={props.isError}
                        chartSettings={props.chartSettings}
                        values={props.values}
                        categories={props.categories}
                        staticColors={props.staticColors}
                onRetry={props.onRetry}
                    />
                }
            </ErrorBoundary>
        </Box>
    )
}

export const RHBarChartCategory = memo((props) => {
    const filtersRegistry = useReportHubFilters();
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Defer rendering the heavy chart to avoid blocking the UI
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        // Defer chart render to next frame to let UI transition complete
        const timeoutId = setTimeout(() => {
            setShouldRender(true);
        }, 0);
        return () => clearTimeout(timeoutId);
    }, []);

    // Memoize the expensive categories mapping
    const mappedCategories = useMemo(() => {
        if (!props.categories) return ["2026-01-01", "2026-01-02", "2026-01-03","2026-01-04","2026-01-05","2026-01-06"];
        return props.categories.map(item => translateChartCategory(item, t));
    }, [props.categories, t]);

    // Calculate dynamic column width based on number of categories and series
    const columnWidth = useMemo(() => {
        const categoryCount = mappedCategories?.length || 1;
        const seriesCount = props.values?.length || 1;
        // For fewer categories, use percentage to fill space better
        // For many categories, use fixed pixel width
        if (categoryCount <= 7) {
            // Use percentage that scales with category count
            const basePercent = Math.min(80, Math.max(30, 100 / categoryCount));
            return `${basePercent}%`;
        }
        // For more categories, use fixed width per bar
        // Use smaller width per series to prevent overlap
        const widthPerSeries = seriesCount > 2 ? 8 : 12;
        return `${widthPerSeries * seriesCount}px`;
    }, [mappedCategories?.length, props.values?.length]);

    // An `all` category holds no values of its own — the backend resolves them and hands them back as
    // the chart's categories — so the count that decides "this is a category chart, and its data has
    // arrived" has to come from the response there.
    let categoryValueCount = props.currentReport.category?.values?.length ?? 0;
    if (props.currentReport.category?.all) {
        categoryValueCount = props.categories?.length ?? 0;
    }

    if (!shouldRender) {
        // Show placeholder during deferred render to prevent flash
        return (
            <Box sx={{ height: props.height ?? 300, width: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Empty placeholder - chart will render next frame */}
            </Box>
        );
    }

    return (
        props.currentReport.category.key && (props.currentReport.category?.all || categoryValueCount > 0) ?
            <HorizontalBarChartContentReports
                id="my-chart"
                onRetry={props.onRetry}
                width="100%"
                height={300}
                stacked={false}
                isLoading={(props.isLoading || (categoryValueCount !== props.values?.[0]?.data.length)) ?? false}
                isError={props.isError ?? false}
                useSort={true}
                chartSettings={props.chartSettings}

                values={props.values ?? [
                    { name: "Orders", data: [30, 20, 10,70,40,20] }  // single series with all values
                ]}
                categories={mappedCategories}

                // values={[
                //     { name: "Orders", data: [30, 20, 10,70,40,20] }  // single series with all values
                // ]}
                // categories={["PostNL", "DPD", "Bol.com","GLS","FedEX","UPS"]}
                color="brown"
                // staticColors={[colors.pastel.light.red[300]]}
                staticColors={props.staticColors ?? [colors.pastel.light.red[300]]}
                plotOptions={{
                    bar: {
                        borderRadius: 4,
                        distributed: false,  // colors each bar separately
                        columnWidth: columnWidth,
                        dataLabels: { position: "top" },
                    }
                }}
            />
        :

            <Box
                sx={{
                    height:300,
                    width:1,
                    display:"flex",
                    flexDirection:"column",
                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                    mb:3,
                    mt:1,
                    p:3,
                    borderRadius:6,
                }}
            >
                <Subheading>
                    {t("choose_category")}
                </Subheading>
                <Box
                    sx={{
                        width:1,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap:"8px 16px",
                        "@media screen and (max-width: 63.9375em)" : {
                            gridTemplateColumns: "1fr 1fr",
                        },
                        "@media screen and (max-width: 47.9375em)" : {
                            gridTemplateColumns: "1fr",
                        }
                    }}
                >
                    <RhReportCategory
                        currentReport={props.currentReport}
                        chartOption={props.chartOption}
                        setCurrentReport={props.setCurrentReport}
                        filtersRegistry={filtersRegistry}
                    />
                </Box>
            </Box>
    )
});

export const RHBarCharDates = memo((props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Defer rendering the heavy chart to avoid blocking the UI
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        // Defer chart render to next frame to let UI transition complete
        const timeoutId = setTimeout(() => {
            setShouldRender(true);
        }, 0);
        return () => clearTimeout(timeoutId);
    }, []);

    // Calculate dynamic column width based on number of categories and series
    const columnWidth = useMemo(() => {
        const categoryCount = props.categories?.length || 6;
        const seriesCount = props.values?.length || 1;
        // For fewer categories, use percentage to fill space better
        // For many categories, use fixed pixel width
        if (categoryCount <= 7) {
            // Use percentage that scales with category count
            const basePercent = Math.min(80, Math.max(30, 100 / categoryCount));
            return `${basePercent}%`;
        }
        // For more categories, use fixed width per bar
        // Use smaller width per series to prevent overlap
        const widthPerSeries = seriesCount > 2 ? 8 : 12;
        return `${widthPerSeries * seriesCount}px`;
    }, [props.categories?.length, props.values?.length]);

    if (!shouldRender) {
        // Show placeholder during deferred render to prevent flash
        return (
            <Box sx={{ height: props.height ?? 300, width: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Empty placeholder - chart will render next frame */}
            </Box>
        );
    }

    return (
        <HorizontalBarChartContentReports
            id="my-chart"
            width="100%"
            height={300}
            stacked={false}
            onRetry={props.onRetry}
            isLoading={props.isLoading ?? false}
            preview={props.preview}
            isError={props.isError ?? false}
            chartSettings={props.chartSettings}
            values={props.values ?? [
                { name: "Orders", data: [30, 20, 10,70,40,20] }  // single series with all values
            ]}
            categories={props.categories ?? ["2026-01-01", "2026-01-02", "2026-01-03","2026-01-04","2026-01-05","2026-01-06"]}
            color="brown"
            staticColors={props.staticColors ?? [colors.pastel.light.red[300]]}
            plotOptions={{
                bar: {
                    borderRadius: 4,
                    distributed: false,  // colors each bar separately
                    columnWidth: columnWidth,
                    dataLabels: { position: "top" },
                }
            }}
        />
    )
});